import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, CheckCircle2, FileText, FileSpreadsheet, Mail,
  Loader2, Plus, Trash2,
} from 'lucide-react';
import type { FileSource, SourceType } from './sources';

interface Props {
  source:   FileSource;
  files:    File[];
  loading:  boolean;
  index:    number;
  onAdd:    (newFiles: File[]) => void;
  onRemove: (idx: number) => void;
}

const iconForType = (t: SourceType) =>
  t === 'pdf' ? FileText : t === 'email' ? Mail : FileSpreadsheet;

const SourceCard = ({ source, files, loading, index, onAdd, onRemove }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploaded = files.length > 0;
  const Icon = iconForType(source.type);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (selected.length > 0) onAdd(selected);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`p-3 rounded-xl border transition-all ${
        uploaded
          ? 'border-green-200 bg-green-50/50'
          : 'border-gray-100 bg-white hover:border-[#1B365D]/20'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          uploaded ? 'bg-green-100' : source.type === 'email' ? 'bg-violet-50' : 'bg-gray-50'
        }`}>
          {uploaded
            ? <CheckCircle2 className="w-4 h-4 text-green-600" />
            : <Icon className={`w-4 h-4 ${source.type === 'email' ? 'text-violet-400' : 'text-[#1B365D]/50'}`} />
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#1B365D]">{source.label}</span>
            {source.multi && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-500 border border-violet-100">
                multi
              </span>
            )}
            {source.optional && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-500 border border-gray-200">
                opzionale
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-400 truncate">
            {uploaded && !source.multi
              ? files[0].name
              : uploaded && source.multi
                ? `${files.length} email caricat${files.length === 1 ? 'a' : 'e'}`
                : source.desc}
          </div>
        </div>

        {/* Action */}
        <input
          ref={inputRef}
          type="file"
          accept={source.accept}
          multiple={source.multi}
          className="hidden"
          onChange={handleChange}
        />
        {uploaded && !source.multi ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-green-600 font-medium">Caricato</span>
            <button
              type="button"
              onClick={() => onRemove(0)}
              className="text-gray-300 hover:text-red-500 transition-colors"
              title="Rimuovi"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:border-[#1B365D]/40 hover:text-[#1B365D] transition-all disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : source.multi && uploaded
                ? <Plus className="w-3 h-3" />
                : <Upload className="w-3 h-3" />
            }
            {source.multi && uploaded ? 'Aggiungi' : 'Carica'}
          </button>
        )}
      </div>

      {/* Lista email caricate (solo per multi) */}
      {source.multi && files.length > 0 && (
        <ul className="mt-2 ml-11 space-y-1">
          {files.map((f, fi) => (
            <li key={fi} className="flex items-center gap-2 text-[11px] text-gray-600 bg-white/70 rounded px-2 py-1 border border-green-100">
              <Mail className="w-3 h-3 text-violet-400 shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemove(fi)}
                className="shrink-0 text-gray-300 hover:text-red-500 transition-colors"
                title="Rimuovi"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
};

export default SourceCard;
